import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaDownload,
  FaExclamationTriangle,
  FaFileContract,
  FaShieldAlt,
  FaSignature,
  FaSpinner
} from 'react-icons/fa';

type PublicContract = {
  id: string;
  subject?: string | null;
  content?: string | null;
  status?: 'pending' | 'signed' | 'expired' | 'cancelled' | string;
  expiry_date?: string | null;
  require_signature?: boolean | null;
  recipient_name?: string | null;
  recipient_email?: string | null;
  recipient_company?: string | null;
  signed_at?: string | null;
  signed_by?: string | null;
  signature_data?: string | null;
  business?: {
    name?: string | null;
    contact_email?: string | null;
  } | null;
};

const isExpired = (expiryDate?: string | null) => {
  if (!expiryDate) return false;
  return new Date(expiryDate).getTime() < Date.now();
};

const getReadableDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('fr-CA');
  } catch {
    return value;
  }
};

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

export default function SignContract() {
  const { contractId } = useParams<{ contractId: string }>();
  const [contract, setContract] = useState<PublicContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signed, setSigned] = useState(false);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  const [signatureHasInk, setSignatureHasInk] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  useEffect(() => {
    fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => initializeCanvas());
    return () => window.cancelAnimationFrame(raf);
  }, [contract?.id]);

  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const cssWidth = Math.max(320, Math.round(rect.width || 720));
    const cssHeight = Math.max(176, Math.round(rect.height || 260));
    const dpr = Math.max(window.devicePixelRatio || 1, 1);

    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);
    canvas.style.width = '100%';
    canvas.style.height = `${cssHeight}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cssWidth, cssHeight);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 3.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = 'rgba(15, 23, 42, 0.12)';
    ctx.shadowBlur = 0.7;
    setSignatureData('');
    setSignatureHasInk(false);
  };

  const fetchContract = async () => {
    if (!contractId) {
      setError('ID de contrat manquant.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('get-sign-contract', {
        body: { contractId },
      });

      if (functionError || data?.success === false) {
        throw new Error(
          data?.error ||
            functionError?.message ||
            "Impossible de charger le contrat. Vérifiez que l'Edge Function get-sign-contract est déployée."
        );
      }

      const publicContract: PublicContract = data.contract;
      if (!publicContract) {
        throw new Error('Contrat non trouvé.');
      }

      const normalizedContract =
        isExpired(publicContract.expiry_date) && publicContract.status === 'pending'
          ? { ...publicContract, status: 'expired' }
          : publicContract;

      setContract(normalizedContract);
      setSigned(normalizedContract.status === 'signed');
    } catch (err: any) {
      setError(err?.message || 'Erreur lors du chargement du contrat.');
    } finally {
      setLoading(false);
    }
  };

  const getCanvasCoordinates = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const refreshSignaturePreview = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSignatureData(canvas.toDataURL('image/png'));
  };

  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.setPointerCapture?.(e.pointerId);
    isDrawingRef.current = true;

    const { x, y } = getCanvasCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.15, y + 0.15);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 3.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    setSignatureHasInk(true);
    refreshSignaturePreview();
  };

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCanvasCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setSignatureHasInk(true);
    refreshSignaturePreview();
  };

  const stopDrawing = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    if (e) {
      try {
        canvas.releasePointerCapture?.(e.pointerId);
      } catch {
        // Certains navigateurs relâchent automatiquement le pointer capture.
      }
    }

    isDrawingRef.current = false;
    refreshSignaturePreview();
  };

  const clearSignature = () => {
    initializeCanvas();
  };

  const downloadSignature = () => {
    if (!signatureData && !contract?.signature_data) return;

    const link = document.createElement('a');
    link.href = signatureData || contract?.signature_data || '';
    link.download = `signature-${contractId || 'forfeo'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getContractPdfHtml = (source: PublicContract | null) => {
    const currentSignature = source?.signature_data || signatureData || '';
    const signUrl =
      typeof window !== 'undefined' && contractId
        ? `${window.location.origin}/sign/${contractId}`
        : '';

    const signatureBlock = currentSignature
      ? `<div style="margin-top:18px;border:1px solid #dbe3ef;border-radius:14px;padding:14px;background:#fff;">
           <div style="font-size:11px;text-transform:uppercase;letter-spacing:.12em;color:#64748b;font-weight:800;margin-bottom:8px;">Signature enregistrée</div>
           <img src="${currentSignature}" style="max-width:260px;max-height:120px;object-fit:contain;border-bottom:1px solid #cbd5e1;" />
           <div style="font-size:12px;color:#475569;margin-top:10px;">
             Signé par ${escapeHtml(source?.signed_by || source?.recipient_name || source?.recipient_email || 'Signataire')}
             ${source?.signed_at ? ` le ${escapeHtml(getReadableDate(source.signed_at))}` : ''}
           </div>
         </div>`
      : `<div style="margin-top:18px;border:1px dashed #cbd5e1;border-radius:14px;padding:18px;color:#64748b;background:#f8fafc;">
           Signature en attente.
         </div>`;

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(source?.subject || 'Contrat Forfeo Sign')}</title>
  <style>
    @page { size: Letter; margin: 18mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #0f172a; background: #ffffff; }
    .page { width: 100%; max-width: 820px; margin: 0 auto; }
    .header { padding: 22px 24px; border-radius: 22px; background: linear-gradient(135deg,#4f46e5,#7c3aed); color: white; margin-bottom: 24px; }
    .brand { font-size: 11px; letter-spacing: .16em; text-transform: uppercase; font-weight: 900; opacity: .9; }
    h1 { font-size: 25px; line-height: 1.25; margin: 8px 0 0; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 18px 0 24px; }
    .box { border: 1px solid #e2e8f0; border-radius: 16px; padding: 14px; background: #f8fafc; }
    .label { font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: #64748b; font-weight: 900; margin-bottom: 6px; }
    .value { font-size: 13px; font-weight: 700; color: #0f172a; }
    .content { white-space: pre-wrap; font-family: "Courier New", monospace; font-size: 12px; line-height: 1.65; border: 1px solid #e2e8f0; border-radius: 18px; padding: 22px; background: #ffffff; }
    .footer { margin-top: 22px; color: #64748b; font-size: 11px; line-height: 1.5; border-top: 1px solid #e2e8f0; padding-top: 14px; }
    .no-print { margin: 0 auto 18px; max-width: 820px; display: flex; gap: 10px; justify-content: flex-end; }
    .no-print button { border: 0; border-radius: 12px; padding: 12px 16px; font-weight: 900; cursor: pointer; }
    .primary { background: #4f46e5; color: white; }
    .secondary { background: #e2e8f0; color: #0f172a; }
    @media print { .no-print { display: none !important; } body { background: white; } }
  </style>
</head>
<body>
  <div class="no-print">
    <button class="secondary" onclick="window.close()">Fermer</button>
    <button class="primary" onclick="window.print()">Télécharger / enregistrer en PDF</button>
  </div>
  <main class="page">
    <section class="header">
      <div class="brand">Forfeo Sign</div>
      <h1>${escapeHtml(source?.subject || 'Contrat')}</h1>
    </section>

    <section class="meta">
      <div class="box">
        <div class="label">Émetteur</div>
        <div class="value">${escapeHtml(source?.business?.name || 'Forfeo')}</div>
      </div>
      <div class="box">
        <div class="label">Destinataire</div>
        <div class="value">${escapeHtml(source?.recipient_name || source?.recipient_email || '—')}${source?.recipient_company ? ` — ${escapeHtml(source.recipient_company)}` : ''}</div>
      </div>
      <div class="box">
        <div class="label">Statut</div>
        <div class="value">${escapeHtml(source?.status === 'signed' ? 'Signé' : source?.status === 'pending' ? 'En attente' : source?.status || '—')}</div>
      </div>
      <div class="box">
        <div class="label">Expiration</div>
        <div class="value">${escapeHtml(getReadableDate(source?.expiry_date))}</div>
      </div>
    </section>

    <section class="content">${escapeHtml(source?.content || 'Aucun contenu')}</section>

    ${signatureBlock}

    <section class="footer">
      Document généré depuis Forfeo Sign. Lien public du contrat : ${escapeHtml(signUrl)}
    </section>
  </main>
  <script>setTimeout(function(){ window.focus(); window.print(); }, 700);</script>
</body>
</html>`;
  };

  const openContractPdf = () => {
    const html = getContractPdfHtml(contract);
    const printWindow = window.open('', '_blank', 'width=950,height=900');

    if (!printWindow) {
      alert("Le navigateur a bloqué l'ouverture du PDF. Autorisez les fenêtres contextuelles pour Forfeo Explore.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleSign = async () => {
    if (!signatureData || !contractId) return;

    setSigning(true);
    setError(null);
    setEmailWarning(null);

    try {
      const signedBy = contract?.recipient_name || contract?.recipient_email || 'Signataire';

      const { data, error: functionError } = await supabase.functions.invoke('sign-contract', {
        body: {
          contractId,
          signatureData,
          signedBy,
        },
      });

      if (functionError || data?.success === false) {
        throw new Error(
          data?.error ||
            functionError?.message ||
            "Impossible d'enregistrer la signature. Vérifiez que l'Edge Function sign-contract est déployée."
        );
      }

      if (data?.emailWarning) {
        setEmailWarning(data.emailWarning);
      }

      setContract(prev =>
        prev
          ? {
              ...prev,
              status: 'signed',
              signed_at: data?.contract?.signed_at || new Date().toISOString(),
              signed_by: signedBy,
              signature_data: signatureData,
            }
          : prev
      );
      setSigned(true);
    } catch (err: any) {
      setError('Erreur lors de la signature : ' + (err?.message || 'Erreur inconnue'));
    } finally {
      setSigning(false);
    }
  };

  const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="min-h-screen bg-slate-50 px-4 py-8 pb-32">
      <div className="mx-auto max-w-5xl">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-black uppercase tracking-widest text-slate-500 shadow-sm ring-1 ring-slate-100 transition hover:text-indigo-600"
        >
          <FaArrowLeft /> Retour
        </Link>
        {children}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Shell>
        <div className="flex min-h-[55vh] items-center justify-center">
          <div className="text-center">
            <FaSpinner className="mx-auto mb-4 animate-spin text-4xl text-indigo-600" />
            <p className="font-bold text-slate-600">Chargement du contrat...</p>
          </div>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="mx-auto max-w-md rounded-3xl bg-white p-8 text-center shadow-xl">
          <FaExclamationTriangle className="mx-auto mb-4 text-5xl text-red-500" />
          <h1 className="mb-2 text-2xl font-black text-slate-900">Erreur</h1>
          <p className="mb-6 text-slate-600">{error}</p>
          <button
            onClick={fetchContract}
            className="rounded-xl bg-indigo-600 px-6 py-3 font-bold text-white transition hover:bg-indigo-700"
          >
            Réessayer
          </button>
        </div>
      </Shell>
    );
  }

  if (signed || contract?.status === 'signed') {
    return (
      <Shell>
        <div className="mx-auto max-w-md rounded-3xl bg-white p-10 text-center shadow-xl">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
            <FaCheckCircle className="text-4xl text-green-600" />
          </div>
          <h1 className="mb-4 text-3xl font-black text-slate-900">Contrat signé !</h1>
          <p className="mb-2 text-slate-600">Votre signature a été enregistrée avec succès.</p>
          {emailWarning ? (
            <div className="my-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left text-sm font-semibold text-amber-800">
              Signature enregistrée, mais l'envoi de confirmation doit être vérifié : {emailWarning}
            </div>
          ) : (
            <p className="mb-6 text-sm text-slate-500">Une confirmation a été envoyée aux parties.</p>
          )}
          {contract?.business?.name && (
            <p className="mb-6 text-sm text-slate-400">Contrat signé avec {contract.business.name}</p>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={openContractPdf}
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-slate-200 px-8 py-4 font-black uppercase tracking-widest text-slate-700 transition hover:bg-slate-50"
            >
              <FaDownload /> Télécharger le PDF
            </button>
            <Link
              to="/"
              className="inline-block rounded-xl bg-indigo-600 px-8 py-4 font-black uppercase tracking-widest text-white transition hover:bg-indigo-700"
            >
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  if (contract?.status === 'expired') {
    return (
      <Shell>
        <div className="mx-auto max-w-md rounded-3xl bg-white p-8 text-center shadow-xl">
          <FaExclamationTriangle className="mx-auto mb-4 text-5xl text-orange-500" />
          <h1 className="mb-2 text-2xl font-black text-slate-900">Contrat expiré</h1>
          <p className="mb-6 text-slate-600">
            Ce contrat a expiré le {getReadableDate(contract.expiry_date)}.
          </p>
          <p className="text-sm text-slate-500">
            Veuillez contacter {contract.business?.name || "l'émetteur"} pour un nouveau contrat.
          </p>
        </div>
      </Shell>
    );
  }

  if (contract?.status === 'cancelled') {
    return (
      <Shell>
        <div className="mx-auto max-w-md rounded-3xl bg-white p-8 text-center shadow-xl">
          <FaExclamationTriangle className="mx-auto mb-4 text-5xl text-slate-400" />
          <h1 className="mb-2 text-2xl font-black text-slate-900">Contrat annulé</h1>
          <p className="text-slate-600">Ce contrat a été annulé par l'émetteur.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6 overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-white shadow-xl">
        <div className="mb-2 flex items-center gap-3">
          <FaFileContract className="text-3xl" />
          <span className="text-sm font-bold uppercase tracking-widest opacity-80">Forfeo Sign</span>
        </div>
        <h1 className="mb-2 text-3xl font-black md:text-4xl">{contract?.subject || 'Contrat'}</h1>
        {contract?.business?.name && <p className="text-indigo-100">De : {contract.business.name}</p>}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-lg">
            <div className="border-b border-slate-100 bg-slate-50 p-6">
              <h2 className="flex items-center gap-2 text-lg font-black text-slate-900">
                <FaFileContract className="text-indigo-600" /> Contenu du contrat
              </h2>
            </div>
            <div className="p-6">
              <div className="whitespace-pre-wrap rounded-2xl border border-slate-200 bg-slate-50 p-6 font-mono text-sm leading-relaxed text-slate-700">
                {contract?.content || 'Aucun contenu'}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-6">
            <h3 className="mb-2 flex items-center gap-2 font-bold text-blue-900">
              <FaShieldAlt /> Consentement et signature électronique
            </h3>
            <p className="text-sm leading-relaxed text-blue-700">
              En signant ce document, vous confirmez votre consentement à utiliser une signature
              électronique pour ce contrat Forfeo Sign. Une trace de signature est enregistrée avec le
              document afin de faciliter la vérification par les parties.
            </p>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="relative z-10 rounded-3xl border border-slate-100 bg-white p-6 shadow-lg lg:sticky lg:top-6">
            <h2 className="mb-6 flex items-center gap-2 text-lg font-black text-slate-900">
              <FaSignature className="text-indigo-600" /> Votre signature
            </h2>

            <div className="mb-4">
              <div className="overflow-hidden rounded-2xl border-2 border-slate-300 bg-white">
                <canvas
                  ref={canvasRef}
                  width={720}
                  height={260}
                  className="block h-44 w-full cursor-crosshair touch-none select-none bg-white"
                  style={{ touchAction: 'none' }}
                  onPointerDown={startDrawing}
                  onPointerMove={draw}
                  onPointerUp={stopDrawing}
                  onPointerLeave={stopDrawing}
                  onPointerCancel={stopDrawing}
                />
              </div>
              <p className="mt-2 text-center text-xs text-slate-400">Votre signature apparaît en temps réel. Vous pouvez l'effacer avant de confirmer.</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={clearSignature}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 py-3 font-bold text-slate-600 transition hover:bg-slate-50"
              >
                Effacer
              </button>

              <button
                onClick={downloadSignature}
                disabled={!signatureHasInk || !signatureData}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 py-3 font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaDownload /> Télécharger la signature
              </button>

              <button
                onClick={openContractPdf}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-200 py-3 font-bold text-slate-600 transition hover:bg-slate-50"
              >
                <FaDownload /> Télécharger le contrat PDF
              </button>

              <button
                onClick={handleSign}
                disabled={!signatureHasInk || !signatureData || signing}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-4 font-black uppercase tracking-widest text-white shadow-lg transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {signing ? (
                  <>
                    <FaSpinner className="animate-spin" /> Traitement...
                  </>
                ) : (
                  <>
                    <FaSignature /> Signer le contrat
                  </>
                )}
              </button>
            </div>

            <div className="mt-6 border-t border-slate-100 pt-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Signataire</p>
              <p className="font-bold text-slate-900">
                {contract?.recipient_name || contract?.recipient_email || 'Signataire'}
              </p>
              {contract?.recipient_company && (
                <p className="text-sm text-slate-500">{contract.recipient_company}</p>
              )}
            </div>

            {contract?.expiry_date && (
              <div className="mt-4 rounded-xl border border-yellow-100 bg-yellow-50 p-4">
                <p className="text-center text-xs font-medium text-yellow-700">
                  ⏰ Expire le {getReadableDate(contract.expiry_date)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}
